// Port of SuiteStyle/OverusedWords.swift (a built-in style check, off by default). Two kinds, both candidates only:
//   - Crutch words (exact): fillers and stock gestures, each with a generous ceiling in uses per 1,000 words — the app's
//     rough guide, not a measured norm. Over its ceiling across the manuscript, flagged once, in the scene using it most.
//   - Crowded scenes (APPROXIMATE without a real tagger: parts of speech): a describing word (adjective, adverb or verb)
//     used several times in one scene but rarely in the rest of the manuscript. Nouns are a scene's subject and left out.
//     Where two words are equally out of proportion the Swift's order is its dictionary's (unspecified); here, by word.
import { COMMON_WORDS } from '../../text/wordFrequency.ts';
import { charIsLowercase, type Range16 } from '../../text/scalars.ts';
import { PROSE_WORD } from '../../text/proseWords.ts';
import { graphemes } from '../../unicode.ts';
import type { LanguageTagger, LexicalClass } from '../../text/tagger.ts';
import { resolvePending, splitParagraphs, STYLE_COMMON, type Pending, type StyleFinding, type StyleText } from './linter.ts';

export interface Crutch {
  label: string;
  forms: Set<string>;
  /** Uses per 1,000 words above which it's worth a look. */
  ceiling: number;
}

const crutch = (label: string, ceiling: number, forms = ''): Crutch =>
  ({ label, forms: new Set([label, ...forms.split(' ').filter(Boolean)]), ceiling });

/** Rough guides, generous on purpose: they flag heavy use, not ordinary use. */
export const CRUTCHES: readonly Crutch[] = [
  crutch('just', 2.5), crutch('really', 1.0), crutch('very', 1.0), crutch('actually', 0.5),
  crutch('suddenly', 0.3), crutch('quite', 0.6), crutch('rather', 0.6), crutch('somehow', 0.3),
  crutch('simply', 0.4), crutch('literally', 0.2), crutch('basically', 0.2), crutch('totally', 0.3),
  crutch('completely', 0.4), crutch('absolutely', 0.3), crutch('definitely', 0.3), crutch('certainly', 0.4),
  crutch('probably', 0.6), crutch('almost', 0.8), crutch('nearly', 0.5), crutch('slightly', 0.4),
  crutch('maybe', 0.8), crutch('perhaps', 0.5), crutch('even', 1.5), crutch('still', 1.5),
  crutch('then', 3.0), crutch('start', 0.6, 'started starting starts'), crutch('begin', 0.6, 'began begun beginning begins'),
  crutch('seem', 0.8, 'seemed seeming seems'), crutch('feel', 1.5, 'felt feeling feels'),
  crutch('look', 1.5, 'looked looking looks'), crutch('turn', 0.8, 'turned turning turns'),
  crutch('nod', 0.3, 'nodded nodding nods'), crutch('shrug', 0.2, 'shrugged shrugging shrugs'),
  crutch('smile', 0.6, 'smiled smiling smiles'), crutch('grin', 0.3, 'grinned grinning grins'),
  crutch('sigh', 0.2, 'sighed sighing sighs'), crutch('glance', 0.3, 'glanced glancing glances'),
  crutch('stare', 0.4, 'stared staring stares'), crutch('gaze', 0.2, 'gazed gazing gazes'),
  crutch('frown', 0.2, 'frowned frowning frowns'), crutch('blink', 0.15, 'blinked blinking blinks'),
  crutch('realise', 0.3, 'realised realising realises realize realized realizing realizes'),
  crutch('moment', 0.5, 'moments'), crutch('eyes', 1.2), crutch('okay', 0.8), crutch('yeah', 1.0),
];

interface Word { key: string; range: Range16 }

function words(text: string): Word[] {
  return [...text.matchAll(PROSE_WORD)].map((m) => ({ key: m[0].toLowerCase().replaceAll('’', "'"), range: [m.index!, m.index! + m[0].length] }));
}

/** `Int.formatted()` (en: grouped thousands). */
const formatted = (n: number) => n.toLocaleString('en-US');
/** `String(format: "%.1f")`. */
const oneDecimal = (x: number) => x.toFixed(1);

const DESCRIBING: ReadonlySet<LexicalClass> = new Set(['adjective', 'adverb', 'verb']);

/** `OverusedWords.check`. */
export function overusedWords(texts: readonly StyleText[], names: ReadonlySet<string>, tagger: LanguageTagger): StyleFinding[] {
  const scenes = texts.map((t) => ({ text: t, words: words(t.text) }));
  const total = scenes.reduce((n, s) => n + s.words.length, 0);
  if (total < 1000) return [];
  const counts = new Map<string, number>();
  for (const s of scenes) for (const w of s.words) counts.set(w.key, (counts.get(w.key) ?? 0) + 1);
  const pending = new Map<number, Pending[]>(); // by scene
  const finding = (scene: number, range: Range16, message: string) => {
    const list = pending.get(scene) ?? [];
    list.push({ check: 'overused-words', range, message });
    pending.set(scene, list);
  };

  // Crutch words across the manuscript, flagged once in the scene that uses them most.
  for (const c of CRUTCHES) {
    let n = 0;
    for (const f of c.forms) n += counts.get(f) ?? 0;
    const rate = (n * 1000) / total;
    if (!(n >= 10 && rate > c.ceiling)) continue;
    let best: { scene: number; hits: Word[] } | null = null;
    scenes.forEach((s, i) => {
      const hits = s.words.filter((w) => c.forms.has(w.key));
      if (!best || hits.length > best.hits.length) best = { scene: i, hits };
    });
    const chosen = best as { scene: number; hits: Word[] } | null;
    if (!chosen || !chosen.hits.length) continue;
    const forms = c.forms.size > 1 ? `“${c.label}” and its forms` : `“${c.label}”`;
    finding(chosen.scene, chosen.hits[0].range,
      `${forms}: ${formatted(n)} times, ${oneDecimal(rate)} per 1,000 words; the rough guide is ${String(c.ceiling)}. Most here: ${chosen.hits.length} in this scene.`);
  }

  // Words that crowd one scene compared with the rest of the manuscript.
  const crutchForms = new Set(CRUTCHES.flatMap((c) => [...c.forms]));
  const skip = new Set([...STYLE_COMMON, ...COMMON_WORDS, ...crutchForms, ...names]);
  scenes.forEach(({ text: scene, words: ws }, index) => {
    if (ws.length < 300) return;
    const local = new Map<string, Word[]>();
    for (const w of ws) {
      if (graphemes(w.key).length < 4 || skip.has(w.key)) continue;
      const list = local.get(w.key);
      if (list) list.push(w); else local.set(w.key, [w]);
    }
    const rest = total - ws.length;
    const crowded: { key: string; hits: Word[]; expected: number }[] = [];
    for (const [key, hits] of local) {
      // Names and other capitalised words are left alone.
      const first = scene.text.slice(hits[0].range[0], hits[0].range[1]);
      if (hits.length < 5 || !charIsLowercase(graphemes(first)[0])) continue;
      const expected = (((counts.get(key) ?? 0) - hits.length) / rest) * ws.length;
      if (!(hits.length >= 4 * Math.max(expected, 0.5))) continue;
      // Describing words only, judged by how most of its uses here are tagged.
      const tags = hits.map((h) => tagger.lexicalClass(scene.text, h.range)).filter((t): t is LexicalClass => t !== null);
      if (!(tags.filter((t) => DESCRIBING.has(t)).length * 2 > tags.length)) continue;
      crowded.push({ key, hits, expected });
    }
    const ratio = (c: { hits: Word[]; expected: number }) => c.hits.length / Math.max(c.expected, 0.5);
    crowded.sort((a, b) => ratio(b) - ratio(a) || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    for (const { key, hits, expected } of crowded.slice(0, 2)) {
      const elsewhere = expected < 0.1 ? 'hardly at all elsewhere' : `about ${oneDecimal(expected)} in a scene this long elsewhere`;
      finding(index, hits[0].range, `“${key}” ${hits.length} times in this scene, against ${elsewhere}.`);
    }
  });
  // Offsets and sentences resolved once per scene.
  return [...pending.keys()].sort((a, b) => a - b).flatMap((i) => resolvePending(pending.get(i)!, scenes[i].text, splitParagraphs(scenes[i].text.text)));
}
